import { VuexResponse } from '@/helpers/vuex-action-builder'
import BookingViewModel from '@/models/bookings/bookingViewModel'
import PendingBookingSummaryViewModel from '@/models/bookings/pendingBookingSummaryViewModel'
import LoadingKeys from '@/shared/constants/core/LoadingKeys'
import { DebouncedBatchQueue } from '@/services/queue/DebouncedBatchQueue'

const ApprovalAction = Object.freeze({
  approve: 'accept',
  decline: 'reject',
})

let approvalQueue = null

const getDefaultState = () => {
  return {
    flyout: {
      booking: {},
      show: false,
      error: false,
    },
    batchFinalizedAt: null,
    pendingBookingIds: [],
    loadingSubmitPendingApproval: false,
  }
}

const state = getDefaultState()

export default {
  namespaced: true,
  state,
  getters: {
    moduleName: () => 'booking-approvals',
    isLoadingApprovalsList: (state, getters, rootState, rootGetters) =>
      rootGetters.isLoadingByKey(
        LoadingKeys.bookingApprovals.loadingApprovalCount
      ),
    isLoadingFlyout: (state, getters, rootState, rootGetters) =>
      rootGetters.isLoadingByKey(
        LoadingKeys.bookingApprovals.loadingApprovalDetails
      ),
    activeBooking: (state) => state.flyout.booking,
    showFlyout: (state) => state.flyout.show,
    flyoutHasError: (state) => state.flyout.error,
    showDetailsContent: (state) => !!state.flyout.booking?.id,
    isLoadingSubmitPendingApproval: (state) =>
      state.loadingSubmitPendingApproval,
    batchFinalizedAt: (state) => state.batchFinalizedAt,
    isBookingPending: (state) => (bookingId) =>
      state.pendingBookingIds.includes(bookingId),
    hasAnyApproving: (state) => state.pendingBookingIds.length > 0,
  },
  mutations: {
    SET_FLYOUT(state, { key, value }) {
      state.flyout[key] = value
    },
    RESET_FLYOUT(state) {
      state.flyout = {
        booking: {},
        show: false,
        error: false,
      }
    },
    CLEAR_STORE(state) {
      Object.assign(state, getDefaultState())
    },
    SET_BATCH_FINALIZED(state) {
      state.batchFinalizedAt = Date.now()
    },
    ADD_PENDING_BOOKING(state, bookingId) {
      if (!state.pendingBookingIds.includes(bookingId)) {
        state.pendingBookingIds.push(bookingId)
      }
    },
    CLEAR_PENDING_BOOKINGS(state) {
      state.pendingBookingIds = []
    },
    // Rename to START_DECLINE_LOADING and FINISH_DECLINE_LOADING when removing legacy submit pending approval
    START_PENDING_APPROVAL_LOADING(state) {
      state.loadingSubmitPendingApproval = true
    },
    FINISH_PENDING_APPROVAL_LOADING(state) {
      state.loadingSubmitPendingApproval = false
    },
  },
  actions: {
    async loadPendingApprovals({ commit }) {
      return await new VuexResponse(commit)
        .request(() => this.$api.pendingBookings.get())
        .withLoadingByKey(LoadingKeys.bookingApprovals.loadingApprovalCount)
        .transform((data) =>
          data.map((item) => new PendingBookingSummaryViewModel(item))
        )
        .go()
    },
    async loadApprovalDetails({ commit }, bookingId) {
      return await new VuexResponse(commit)
        .request(() => this.$api.bookings.get(bookingId))
        .withLoadingByKey(LoadingKeys.bookingApprovals.loadingApprovalDetails)
        .transform((data) => new BookingViewModel(data))
        .go()
    },
    async legacySubmitPendingApproval({ commit, dispatch }, payload) {
      // to support old pending approvals page
      return await new VuexResponse(commit)
        .request(() => this.$api.pendingBookings.action(payload))
        .withLoading(
          'START_PENDING_APPROVAL_LOADING',
          'FINISH_PENDING_APPROVAL_LOADING'
        )
        .withSuccessToast(
          payload.action === 'accept'
            ? this.$i18n.t('booking.details.approvals.submit.acceptToast', {
                companyNameShort: this.$i18n.t('app.companyNameShort'),
              })
            : this.$i18n.t('booking.details.approvals.submit.rejectToast')
        )
        .withFailureToast(
          this.$i18n.t('booking.details.approvals.submit.failureToast')
        )
        .go()
    },
    async declinePendingApproval({ commit }, { reason, bookingId }) {
      const payload = {
        action: ApprovalAction.decline,
        reason,
        bookingId,
      }

      return await new VuexResponse(commit)
        .request(() => this.$api.pendingBookings.action(payload))
        .withSuccessToast(
          this.$i18n.t('booking.details.approvals.submit.rejectToast', [
            this.$i18n.t('app.companyNameShort'),
          ])
        )
        .withFailureToast(
          this.$i18n.t('booking.details.approvals.submit.failureToast')
        )
        .withLoading(
          'START_PENDING_APPROVAL_LOADING',
          'FINISH_PENDING_APPROVAL_LOADING'
        )
        .go()
    },
    createApprovalQueue({ commit }) {
      const processor = (item) =>
        new VuexResponse(commit)
          .request(() => this.$api.pendingBookings.action(item))
          .go()

      const onComplete = ({ successCount }) => {
        commit('CLEAR_PENDING_BOOKINGS')
        if (successCount > 0) {
          commit('SET_BATCH_FINALIZED')
        }
      }

      approvalQueue = new DebouncedBatchQueue({
        idKey: 'bookingId',
        toasts: createApprovalToasts(this.$i18n),
        processor,
        onComplete,
      })
    },
    async queueApproval({ dispatch, commit }, bookingId) {
      if (!approvalQueue) {
        await dispatch('createApprovalQueue')
      }

      commit('ADD_PENDING_BOOKING', bookingId)

      approvalQueue.add({
        action: ApprovalAction.approve,
        bookingId,
      })
    },
    openFlyout({ commit }) {
      commit('SET_FLYOUT', { key: 'show', value: true })
    },
    setFlyout({ commit }, data) {
      commit('SET_FLYOUT', data)
    },
    resetFlyout({ commit }) {
      return commit('RESET_FLYOUT')
    },
    /**
     * Resets store to default state.
     */
    clear({ commit }) {
      if (approvalQueue) {
        approvalQueue.clear()
      }
      commit('CLEAR_STORE')
    },
  },
}

const createApprovalToasts = (i18n) => {
  const companyNameShort = i18n.t('app.companyNameShort')

  const success = (count) => {
    if (count === 1) {
      return i18n.t('booking.details.approvals.submit.acceptToast', {
        companyNameShort,
      })
    }

    return i18n.t('booking.details.approvals.submit.acceptToastPlural', {
      count,
      companyNameShort,
    })
  }

  const failure = () => i18n.t('booking.details.approvals.submit.failureToast')

  return {
    success,
    failure,
  }
}
